import { Extension } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { CellReference } from './cellReferences';

export const FORMULA_CELL_HIGHLIGHT_TIPTAP_EXTENSION_NAME = 'formulaCellHighlight';
export const formulaCellHighlightPluginKey = new PluginKey<DecorationSet>(FORMULA_CELL_HIGHLIGHT_TIPTAP_EXTENSION_NAME);

type FormulaCellHighlightMeta = {
    cellReferences: CellReference[];
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        formulaCellHighlight: {
            applyFormulaCellHighlights: (cellReferences: CellReference[]) => ReturnType;
            clearFormulaCellHighlights: () => ReturnType;
        };
    }
}

const getHighlightAttrs = ({ borderColorHex }: CellReference) => ({
    style: `color: #${borderColorHex}`,
});

/**
 * Builds inline highlights for formula cells from parsed cell references.
 */
export const getFormulaCellHighlights = (doc: Node, cellReferences: CellReference[]): Decoration[] => {
    if (cellReferences.length === 0) return [];

    const highlights: Decoration[] = [];
    let plainTextOffset = 0;
    let hasSeenTextBlock = false;

    doc.descendants((node, pos) => {
        // Keep offsets aligned with getTiptapNodeTextContent(), which joins text blocks with "\n".
        if (node.isTextblock) {
            if (hasSeenTextBlock) plainTextOffset += 1;
            hasSeenTextBlock = true;
            return true;
        }

        if (node.type.name === 'hardBreak') {
            plainTextOffset += 1;
            return false;
        }

        if (!node.isText) return true;

        const text = node.text || '';
        const textStart = plainTextOffset;
        const textEnd = textStart + text.length;

        cellReferences.forEach((cellReference) => {
            const from = Math.max(cellReference.from, textStart);
            const to = Math.min(cellReference.to, textEnd);

            if (from >= to) return;

            highlights.push(
                Decoration.inline(
                    pos + (from - textStart),
                    pos + (to - textStart),
                    getHighlightAttrs(cellReference),
                    cellReference,
                ),
            );
        });

        plainTextOffset = textEnd;
        return false;
    });

    return highlights;
};

const createFormulaCellHighlightPlugin = () =>
    new Plugin<DecorationSet>({
        key: formulaCellHighlightPluginKey,
        state: {
            init: () => DecorationSet.empty,
            apply: (transaction, oldState) => {
                const highlightMeta = transaction.getMeta(formulaCellHighlightPluginKey) as
                    | FormulaCellHighlightMeta // apply highlights
                    | null // clear highlights
                    | undefined; // no changes

                if (highlightMeta === undefined) return oldState.map(transaction.mapping, transaction.doc);
                if (!highlightMeta || highlightMeta.cellReferences.length === 0) return DecorationSet.empty;

                return DecorationSet.create(
                    transaction.doc,
                    getFormulaCellHighlights(transaction.doc, highlightMeta.cellReferences),
                );
            },
        },
        props: {
            decorations(state) {
                return this.getState(state);
            },
        },
    });

/**
 * Adds commands for applying inline formula cell highlights to a Tiptap editor.
 */
const FormulaCellHighlightTiptapExtension = Extension.create({
    name: FORMULA_CELL_HIGHLIGHT_TIPTAP_EXTENSION_NAME,

    addCommands() {
        return {
            applyFormulaCellHighlights:
                (cellReferences) =>
                ({ tr, dispatch }) => {
                    if (!dispatch) return true;

                    dispatch(tr.setMeta(formulaCellHighlightPluginKey, { cellReferences }));
                    return true;
                },
            clearFormulaCellHighlights:
                () =>
                ({ tr, dispatch }) => {
                    if (!dispatch) return true;

                    dispatch(tr.setMeta(formulaCellHighlightPluginKey, null));
                    return true;
                },
        };
    },

    addProseMirrorPlugins() {
        return [createFormulaCellHighlightPlugin()];
    },
});

export default FormulaCellHighlightTiptapExtension;
